import { useEffect, useState } from 'react';

/**
 * Resolves as soon as the page can afford expensive, below-the-fold work: either the visitor's
 * first interaction (pointer, touch, wheel, scroll, key) or the first idle slot after the window
 * `load` event. Interaction is the fast path for engaged visitors; the idle-after-load path covers
 * everyone else without waiting on a fixed timer. The `idleTimeout` only bounds how long a busy
 * main thread may defer the idle callback, so a pegged thread still resolves instead of stalling.
 *
 * @param idleTimeout - Max ms the idle callback may be deferred before it fires anyway
 * @param touchIdleDelay - On touch devices, ms after first contentful paint before the idle path may fire
 * @returns True once the page is ready for deferred work; always false during SSR
 */
export function useInteractionReady(idleTimeout = 1500, touchIdleDelay = 0): boolean {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let idleId: number | undefined;
    let delayId: number | undefined;
    let resolved = false;

    function resolve() {
      if (resolved) {
        return;
      }
      resolved = true;
      cancelIdle();
      window.clearTimeout(delayId);
      setReady(true);
    }

    function requestIdle() {
      if ('function' === typeof window.requestIdleCallback) {
        idleId = window.requestIdleCallback(resolve, { timeout: idleTimeout });
      } else {
        idleId = window.setTimeout(resolve, 0);
      }
    }

    function cancelIdle() {
      if (undefined === idleId) {
        return;
      }
      if ('function' === typeof window.cancelIdleCallback) {
        window.cancelIdleCallback(idleId);
      } else {
        window.clearTimeout(idleId);
      }
      idleId = undefined;
    }

    function idleAfterDelay() {
      const isTouch = window.matchMedia('(pointer: coarse)').matches;
      const fcp = performance.getEntriesByName('first-contentful-paint')[0]?.startTime ?? 0;
      const wait = isTouch ? fcp + touchIdleDelay - performance.now() : 0;
      if (0 >= wait) {
        requestIdle();
        return;
      }
      delayId = window.setTimeout(requestIdle, wait);
    }

    function onLoad() {
      idleAfterDelay();
    }

    const options = { passive: true };
    window.addEventListener('pointermove', resolve, options);
    window.addEventListener('pointerdown', resolve, options);
    window.addEventListener('touchstart', resolve, options);
    window.addEventListener('wheel', resolve, options);
    window.addEventListener('scroll', resolve, options);
    window.addEventListener('keydown', resolve, options);

    if ('complete' === document.readyState) {
      idleAfterDelay();
    } else {
      window.addEventListener('load', onLoad);
    }

    return () => {
      cancelIdle();
      window.clearTimeout(delayId);
      window.removeEventListener('pointermove', resolve);
      window.removeEventListener('pointerdown', resolve);
      window.removeEventListener('touchstart', resolve);
      window.removeEventListener('wheel', resolve);
      window.removeEventListener('scroll', resolve);
      window.removeEventListener('keydown', resolve);
      window.removeEventListener('load', onLoad);
    };
  }, [idleTimeout, touchIdleDelay]);

  return ready;
}
